import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnDef, DataTableComponent } from '../../../shared/components/data-table/data-table';
import { RollupService } from '../../../core/services/rollup.service';
import { RollupTaxonomy } from '../../../shared/models/rollup.models';
import { ClassificationType } from '../../../shared/models/category.models';
import { CreateRollupTaxonomyModalComponent } from './create-rollup-taxonomy-modal';

@Component({
  selector: 'app-rollup-taxonomies',
  imports: [NgClass, DataTableComponent, CreateRollupTaxonomyModalComponent],
  templateUrl: './rollup-taxonomies.html',
})
export class RollupTaxonomiesComponent implements OnInit {
  private rollupService = inject(RollupService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  protected readonly ClassificationType = ClassificationType;
  protected showCreate = signal(false);
  protected rollupTaxonomies = signal<RollupTaxonomy[]>([]);
  protected total = signal(0);
  protected pageIndex = signal(0);
  protected loading = signal(false);
  protected readonly pageSize = 10;
  protected searchQuery = signal('');

  private nameTpl = viewChild.required<TemplateRef<{ $implicit: RollupTaxonomy }>>('nameTpl');
  private categoryTpl =
    viewChild.required<TemplateRef<{ $implicit: RollupTaxonomy }>>('categoryTpl');

  protected readonly columns = computed<ColumnDef<RollupTaxonomy>[]>(() => [
    { key: 'name', header: 'Rollup Name', cellTemplate: this.nameTpl() },
    { key: 'classificationType', header: 'Category', cellTemplate: this.categoryTpl() },
    {
      key: 'sourceTaxonomies',
      header: 'Source Taxonomies',
      cell: (r) => (r.sourceTaxonomies != null ? `${r.sourceTaxonomies} Taxonomies` : '—'),
      cellClass: 'text-navy-900',
    },
    {
      key: 'mappedCategories',
      header: 'Mapped Categories',
      cell: (r) => (r.mappedCategories != null ? `${r.mappedCategories} Categories` : '—'),
      cellClass: 'text-navy-900',
    },
  ]);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.rollupService
      .getRollupTaxonomies({
        pageSize: this.pageSize,
        pageIndex: this.pageIndex(),
        filter: this.searchQuery(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rollupTaxonomies.set(res.rollupTaxonomies ?? []);
          this.total.set(res.total ?? 0);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected onPageChange(page: number): void {
    this.pageIndex.set(page - 1);
    this.load();
  }

  protected onRowAction(row: RollupTaxonomy): void {
    this.router.navigate([
      '/configuration/rollup',
      row.classificationType === ClassificationType.Situation ? 'situation' : 'response',
      row.id,
    ]);
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.pageIndex.set(0);
    this.load();
  }
}
